import { useState, useEffect } from 'react';
import '../styles/Order.css';

const Order = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Debug: Check what's in localStorage
        console.log('All localStorage items:');
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            console.log(`${key}:`, localStorage.getItem(key));
        }

        const fetchOrders = async () => {
            try {
                setLoading(true);
                setError(null);

                // Get user info and check authentication
                const userData = localStorage.getItem('userInfo');
                const loggedIn = localStorage.getItem('loggedIn');

                if (!userData || loggedIn !== 'true') {
                    throw new Error('User not authenticated. Please login again.');
                }

                const userInfo = JSON.parse(userData);
                console.log('User Info:', userInfo);

                // Try to get token from various possible locations
                const token = localStorage.getItem('userToken') ||

                    (userInfo.token ? userInfo.token : null);

                console.log('Available token:', token);

                // Prepare headers
                const headers = {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                };

                // Add authorization header if token exists
                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }

                const response = await fetch('https://akadgatepharmacy.com/backend/public/api/v1/orders', {
                    method: 'GET',
                    headers: headers,
                    credentials: 'include' // Include cookies for session-based auth
                });

                console.log('Response status:', response.status);

                if (!response.ok) {
                    if (response.status === 401) {
                        // Clear storage and redirect to login
                        localStorage.removeItem('userInfo');
                        localStorage.removeItem('auth_token');
                        localStorage.removeItem('token');
                        localStorage.removeItem('sanctum_token');
                        localStorage.removeItem('loggedIn');
                        throw new Error('Authentication failed. Please login again.');
                    }
                    throw new Error(`Failed to fetch orders. Status: ${response.status}`);
                }

                const result = await response.json();

                if (result.success) {
                    setOrders(result.data);
                    console.log('Fetched Orders:', result.data);
                } else {
                    throw new Error(result.message || 'Failed to load orders');
                }
            } catch (err) {
                setError(err.message);
                console.error('Error fetching orders:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, []);

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(parseFloat(price));
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const handleRetry = () => {
        setLoading(true);
        setError(null);
        window.location.reload();
    };

    const handleLoginRedirect = () => {
        localStorage.removeItem('userInfo');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('token');
        localStorage.removeItem('sanctum_token');
        localStorage.removeItem('loggedIn');
        window.location.href = '/login';
    };

    if (loading) {
        return (
            <div className="order-container">
                <div className="loading">
                    <div className="spinner"></div>
                    <p>Loading your orders...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="order-container">
                <div className="error-state">
                    <div className="error-icon">⚠️</div>
                    <h3>Unable to Load Orders</h3>
                    <p>{error}</p>
                    <div className="error-actions">
                        <button onClick={handleRetry} className="btn-retry">
                            Try Again
                        </button>
                        {error.includes('Authentication') && (
                            <button onClick={handleLoginRedirect} className="btn-login">
                                Go to Login
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="order-container" style={{ minHeight: orders.length === 0 ? '70vh' : 'auto' }}>
            <div className="order-header">
                <h1>My Orders</h1>
                <p className="order-subtitle">View your order history and track your purchases</p>
            </div>

            {orders.length === 0 ? (
                <div className="no-orders">
                    <div className="no-orders-icon">📦</div>
                    <h3>No Orders Found</h3>
                    <p>You haven't placed any orders yet.</p>
                    <button onClick={() => window.location.href = '/products'} className="btn-shop">
                        Start Shopping
                    </button>
                </div>
            ) : (
                <div className="orders-list">
                    {orders.map((order) => (
                        <div key={order.id} className="order-card">
                            <div className="order-card-header">
                                <div className="order-main-info">
                                    <h3 className="order-number">Order #{order.invoiceNumber}</h3>
                                    <div className="order-status-container">
                                        <span className={`order-status ${order.status.toLowerCase()}`}>
                                            {order.status}
                                        </span>
                                        <span className="order-date">{formatDate(order.date)}</span>
                                    </div>
                                </div>
                                <div className="order-payment-info">
                                    <span className="payment-method">{order.paymentMethod}</span>
                                    <span className="total-price">${formatPrice(order.totalPrice)}</span>
                                </div>
                            </div>

                            <div className="order-summary">
                                <div className="summary-grid">
                                    <div className="summary-item">
                                        <span className="summary-label">Total Items:</span>
                                        <span className="summary-value">{parseFloat(order.totalQty).toFixed(0)}</span>
                                    </div>
                                    <div className="summary-item">
                                        <span className="summary-label">Status:</span>
                                        <span className={`summary-value status-${order.status.toLowerCase()}`}>
                                            {order.status}
                                        </span>
                                    </div>
                                    <div className="summary-item">
                                        <span className="summary-label">Payment Method:</span>
                                        <span className="summary-value">{order.paymentMethod}</span>
                                    </div>
                                </div>
                            </div>

                            {order.note && (
                                <div className="order-note">
                                    <strong>Delivery Note:</strong> {order.note}
                                </div>
                            )}

                            <div className="order-items-section">
                                <h4 className="items-title">Order Items</h4>
                                <div className="order-items">
                                    {order.sell_orders.map((item) => (
                                        <div key={item.id} className="order-item">
                                            <div className="item-image">
                                                <img
                                                    src={`${item.product.featuredImage.replace('/storage', 'https://akadgatepharmacy.com/backend/storage/app/public')}`}
                                                    alt={item.product.name}
                                                    onError={(e) => {
                                                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjRjBGMEYwIi8+CjxwYXRoIGQ9Ik0zMCAzNUMzMi43NjE0IDM1IDM1IDMyLjc2MTQgMzUgMzBDMzUgMjcuMjM4NiAzMi43NjE0IDI1IDMwIDI1QzI3LjIzODYgMjUgMjUgMjcuMjM4NiAyNSAzMEMyNSAzMi43NjE0IDI3LjIzODYgMzUgMzAgMzVaIiBmaWxsPSIjQ0VDRUNFIi8+CjxwYXRoIGQ9Ik0yNS41IDM1LjVWMzkuNUMzMC4xODY2IDM5LjUgMzQuNSAzNS4xODY2IDM0LjUgMzAuNUMzNC41IDI1LjgxMzQgMzAuMTg2NiAyMS41IDI1LjUgMjEuNVYyNS41QzI4LjUzMTcgMjUuNSAzMSAyNy45NjgzIDMxIDMxQzMxIDM0LjAzMTcgMjguNTMxNyAzNi41IDI1LjUgMzYuNVoiIGZpbGw9IiNDRUNFQ0UiLz4KPC9zdmc+';
                                                    }}
                                                />
                                            </div>
                                            <div className="item-details">
                                                <h5 className="item-name">{item.product.name}</h5>
                                                <p className="item-category">{item.product.category}</p>
                                                <p className="item-description">{item.product.description}</p>
                                            </div>
                                            <div className="item-pricing">
                                                <div className="item-quantity">
                                                    Quantity: {parseFloat(item.qty).toFixed(0)}
                                                </div>
                                                <div className="item-price">
                                                    ${formatPrice(item.price)}
                                                </div>
                                                <div className="item-total">
                                                    Total: ${formatPrice(parseFloat(item.price) * parseFloat(item.qty))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Order;